import React, { useEffect, useState, useRef } from "react";
import secureAxios from "../../../utils/secureAxios";
import { useAuth } from "../../../contexts/AuthContext";
import WidgetCard from "./WidgetCard";

const FlatRateUsageWidget = ({ onlyMyData }) => {
  const { user } = useAuth();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const hasFetchedRef = useRef(false);
  const lastParamsRef = useRef(null);

  useEffect(() => {
    const fetchFlatRateData = async () => {
      if (!user?.user_id || !user?.tenant_id) {
        setLoading(false);
        return;
      }

      const paramsKey = `${user.user_id}-${user.tenant_id}-${onlyMyData}`;

      if (hasFetchedRef.current && lastParamsRef.current === paramsKey) {
        return;
      }

      hasFetchedRef.current = true;
      lastParamsRef.current = paramsKey;
      setLoading(true);

      try {
        const res = await secureAxios.get("/api/widgets/flat-rate-usage", {
          params: {
            only_my_data: onlyMyData,
          },
        });

        setData(res.data || []);
        setError(false);
      } catch (err) {
        if (process.env.NODE_ENV === "development") {
          console.error("Error loading flat rate widget data", err);
        }
        setError(true);
        setData([]);
      } finally {
        setLoading(false);
      }
    };

    fetchFlatRateData();
  }, [user?.user_id, user?.tenant_id, onlyMyData]);

  const formatCurrency = (value) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(parseFloat(value) || 0);
  };

  const formatNumber = (value) => {
    return parseFloat(value || 0).toFixed(2);
  };

  const grandTotal = data.reduce(
    (sum, row) => sum + parseFloat(row.flat_rate_total || 0),
    0
  );

  const subtitle =
    data.length > 0
      ? `${data.length} ${
          data.length === 1 ? "authorization" : "authorizations"
        } • Total: ${formatCurrency(grandTotal)}`
      : undefined;

  const headerRight = user?.is_admin ? (
    <small className="text-muted">
      <i className={`bi bi-${onlyMyData ? "person" : "people"}-fill`}></i>
    </small>
  ) : null;

  return (
    <WidgetCard
      title="Flat-Rate Usage"
      subtitle={!loading && !error && data.length > 0 ? subtitle : undefined}
      icon="bi-box-seam"
      iconColor="orange"
      loading={loading}
      error={error}
      errorMessage="Unable to load data"
      empty={data.length === 0}
      emptyMessage="No flat-rate authorizations found"
      headerRight={headerRight}
    >
      {/* Desktop table view */}
      <div className="d-none d-md-block">
        <div className="table-responsive">
          <table className="table table-sm widget-table">
            <thead>
              <tr>
                <th>Auth #</th>
                <th>Consumer</th>
                <th>Job Coach</th>
                <th className="text-end">Rate</th>
                <th className="text-end">Hours</th>
                <th className="text-end">Total</th>
              </tr>
            </thead>
            <tbody>
              {data.map((row) => (
                <tr key={row.authorization_id}>
                  <td className="widget-auth-number text-nowrap">
                    {row.authorization_number}
                  </td>
                  <td className="widget-consumer-name">{row.consumer_name}</td>
                  <td className="widget-coach-name">
                    {row.job_coach || "Unassigned"}
                  </td>
                  <td className="text-end text-nowrap">
                    {formatCurrency(row.rate)}
                  </td>
                  <td className="text-end text-nowrap">
                    {formatNumber(row.total_hours_worked)}
                  </td>
                  <td className="text-end fw-bold text-orange text-nowrap">
                    {formatCurrency(row.flat_rate_total)}
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="widget-total-row">
                <td colSpan="5" className="text-end fw-bold">
                  Total:
                </td>
                <td className="text-end fw-bold text-orange fs-5">
                  {formatCurrency(grandTotal)}
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>

      {/* Mobile view */}
      <div className="d-block d-md-none">
        {data.map((row, index) => (
          <div
            key={row.authorization_id}
            className={`widget-mobile-card widget-mobile-card--orange border ${
              index < data.length - 1 ? "mb-2" : ""
            }`}
          >
            <div className="d-flex justify-content-between align-items-start mb-2">
              <div>
                <div className="widget-auth-number text-orange mb-1">
                  {row.authorization_number}
                </div>
                <div className="widget-consumer-name small">
                  {row.consumer_name}
                </div>
                <div className="text-muted small">
                  <i className="bi bi-person me-1"></i>
                  {row.job_coach || "Unassigned"}
                </div>
              </div>
              <div className="text-end">
                <div className="fw-bold text-orange fs-5">
                  {formatCurrency(row.flat_rate_total)}
                </div>
              </div>
            </div>
            <div className="d-flex gap-3 mt-2">
              <span className="widget-metric-badge">
                <i className="bi bi-currency-dollar me-1"></i>
                {formatCurrency(row.rate)}/hr
              </span>
              <span className="widget-metric-badge">
                <i className="bi bi-clock me-1"></i>
                {formatNumber(row.total_hours_worked)} hrs
              </span>
            </div>
          </div>
        ))}

        <div className="mt-3 p-3 bg-orange bg-opacity-10 rounded-2">
          <div className="d-flex justify-content-between align-items-center">
            <span className="fw-semibold">Grand Total:</span>
            <span className="fw-bold text-orange fs-4">
              {formatCurrency(grandTotal)}
            </span>
          </div>
        </div>
      </div>
    </WidgetCard>
  );
};

export default FlatRateUsageWidget;
